import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '../../lib/constants';

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, resendVerification } = useAuth(); // 💡 Destructure resendVerification

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false); // 💡 Tracks state of resend button clicks
  const [unverifiedEmail, setUnverifiedEmail] = useState(''); // 💡 Captures email if account needs activation
  const [errors, setErrors] = useState({});

  const from = location.state?.from?.pathname || ROUTES.DASHBOARD;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear out unverified banner text if they change their input fields
    if (unverifiedEmail) setUnverifiedEmail('');
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setUnverifiedEmail(''); // Clear older error instances

    try {
      await signIn(formData.email, formData.password);
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login failed:', error);
      
      // 💡 Intercepting Supabase's native unconfirmed error status string
      if (error.message?.toLowerCase().includes('confirmed') || error.message?.toLowerCase().includes('verification')) {
        setUnverifiedEmail(formData.email);
      } else {
        // Fallback for bad passwords or missing accounts
        setErrors({ form: error.message || 'Invalid email or password' });
      }
    } finally {
      setLoading(false);
    }
  };

  // 💡 Handler for trigger link clicks inside the notification block
  const handleResend = async () => {
    if (!unverifiedEmail) return;
    setResendLoading(true);
    try {
      await resendVerification(unverifiedEmail);
    } catch (err) {
      console.error('Resend process failed:', err);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto px-4 sm:px-6 md:px-8">
      <div className="backdrop-blur-lg bg-white/40 border border-cream-300/50 rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10">
        
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="rounded-full bg-gradient-to-br from-brown-500 to-brown-700 border-4 border-brown-100 shadow-lg flex items-center justify-center aspect-square overflow-hidden w-16 sm:w-18 md:w-20 lg:w-24">
              <img 
                src="/book.png" 
                alt="OmniClarix - Clarity, Simplified." 
                className="w-auto h-4/5 object-contain pointer-events-none"
              />
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-brown-900 mb-1 sm:mb-2">
            Welcome Back
          </h2>
          <p className="text-sm sm:text-base text-brown-600">
            Sign in to continue learning
          </p>
        </div>

        {/* 💡 THE DYNAMIC VERIFICATION REMINDER BANNER */}
        {unverifiedEmail && (
          <div className="mb-6 p-4 rounded-xl border border-amber-200 bg-amber-50/80 backdrop-blur-sm text-amber-900 text-sm flex gap-3 shadow-sm animate-fade-in">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold mb-1">Account not activated yet</p>
              <p className="text-amber-800 leading-relaxed mb-2">
                Please check your <span className="font-medium">Spam or Promotions folder</span> if you can't find it in your main inbox.
              </p>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendLoading}
                className="text-brown-700 font-bold underline hover:text-brown-900 focus:outline-none flex items-center gap-1.5 disabled:opacity-60"
              >
                {resendLoading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Sending fresh link...</span>
                  </>
                ) : (
                  'Click here to resend the activation link'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Global Standard Error Display */}
        {errors.form && (
          <div className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm flex gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{errors.form}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 md:space-y-6">
          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm sm:text-base font-medium text-brown-800 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-brown-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 md:py-3.5 
                  bg-white/60 backdrop-blur-sm border-2 rounded-lg sm:rounded-xl 
                  focus:ring-2 focus:ring-brown-500 focus:border-brown-500 
                  outline-none transition-all text-sm sm:text-base
                  placeholder:text-brown-300 text-brown-900
                  ${errors.email ? 'border-red-400' : 'border-cream-300'}`}
                disabled={loading}
              />
            </div>
            {errors.email && (
              <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm sm:text-base font-medium text-brown-800 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-brown-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                className={`w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-2.5 sm:py-3 md:py-3.5 
                  bg-white/60 backdrop-blur-sm border-2 rounded-lg sm:rounded-xl 
                  focus:ring-2 focus:ring-brown-500 focus:border-brown-500 
                  outline-none transition-all text-sm sm:text-base
                  placeholder:text-brown-300 text-brown-900
                  ${errors.password ? 'border-red-400' : 'border-cream-300'}`}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-brown-400 hover:text-brown-600 transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm text-red-600">{errors.password}</p>
            )}
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex flex-row items-center justify-between gap-2">
            <label className="flex items-center select-none">
              <input
                type="checkbox"
                className="w-4 h-4 text-brown-600 border-cream-300 rounded focus:ring-brown-500 cursor-pointer"
              />
              <span className="ml-2 text-xs sm:text-sm text-brown-700 cursor-pointer">Remember me</span>
            </label>
            <Link
              to={ROUTES.FORGOT_PASSWORD}
              className="text-xs sm:text-sm text-brown-700 hover:text-brown-900 font-medium transition-colors whitespace-nowrap"
            >
              Forgot password?
            </Link>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-brown-600 to-brown-700 hover:from-brown-700 hover:to-brown-800 
              text-white font-medium py-2.5 sm:py-3 md:py-3.5 px-4 rounded-lg sm:rounded-xl 
              transition-all duration-200 flex items-center justify-center 
              disabled:opacity-50 disabled:cursor-not-allowed shadow-lg
              text-sm sm:text-base md:text-lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin" />
                <span>Signing in...</span>
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Sign Up Link */}
        <div className="mt-6 sm:mt-8 text-center">
          <p className="text-xs sm:text-sm text-brown-600">
            Don't have an account?{' '}
            <Link
              to={ROUTES.SIGNUP}
              className="text-brown-700 hover:text-brown-900 font-semibold transition-colors"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;